import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, brl, formatDate } from "../lib/api";
import { computeInstallments, installmentsSum, toISODate } from "../lib/installments";
import { toast } from "sonner";
import { Trash, Plus, WarningCircle } from "@phosphor-icons/react";
import BRLInput from "../components/BRLInput";
import ProductPicker from "../components/ProductPicker";
import { useAuth } from "../contexts/AuthContext";

const REASONS = ["Correção de lançamento", "Troca de produto", "Erro de digitação", "Renegociação", "Outro"];

export default function EditSale() {
  const { saleId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [sale, setSale] = useState(null);
  const [orig, setOrig] = useState(null);
  const [products, setProducts] = useState([]);
  const [pricePolicy, setPricePolicy] = useState("all");
  const [installments, setInstallments] = useState(1);
  const [firstDue, setFirstDue] = useState("");
  const [method, setMethod] = useState("pix");
  const [downPayment, setDownPayment] = useState(0);
  const [saleDate, setSaleDate] = useState("");
  const [discount, setDiscount] = useState(0);
  const [surcharge, setSurcharge] = useState(0);
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [reasonDetail, setReasonDetail] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/sales/${saleId}`);
        setSale(data);
        if (!data.can_edit) return;
        setOrig(data);
        setProducts((data.products || []).map((p) => ({ ...p, catalog_price: p.product_id ? p.price : undefined, price_reason: "" })));
        setInstallments((Array.isArray(data.installments) ? data.installments.length : data.installments) || 1);
        setFirstDue((data.installments?.[0]?.due_date || "").slice(0, 10));
        setMethod(data.payment_method || "pix");
        setDownPayment(data.down_payment || 0);
        setSaleDate((data.sale_date || "").slice(0, 10));
        setDiscount(data.discount || 0);
        setSurcharge(data.surcharge || 0);
        setNotes(data.notes || "");
      } catch (e) { toast.error("Erro ao carregar venda"); }
    })();
  }, [saleId]);

  useEffect(() => { (async () => { try { setPricePolicy((await api.get("/products/sale-settings")).data.price_change_policy || "all"); } catch { /* ignore */ } })(); }, []);

  const canEditPrice = pricePolicy === "all" || (pricePolicy === "admin_only" && user?.role === "admin");

  const addFromCatalog = (prod) => {
    const existing = products.find((p) => p.product_id === prod.id);
    const newQty = existing ? Number(existing.qty || 1) + 1 : 1;
    setProducts((prev) => {
      const idx = prev.findIndex((p) => p.product_id === prod.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = { ...next[idx], qty: Number(next[idx].qty || 1) + 1 }; return next; }
      return [...prev, { name: prod.nome_produto, qty: 1, price: Number(prod.valor_venda) || 0,
        product_id: prod.id, barcode: prod.codigo_barras || "", codigo_interno: prod.codigo_interno || "", catalog_price: Number(prod.valor_venda) || 0, price_reason: "" }];
    });
    return newQty;
  };

  const subtotal = products.reduce((a, p) => a + Number(p.qty || 0) * Number(p.price || 0), 0);
  const total = Math.max(0, subtotal - Number(discount || 0) + Number(surcharge || 0));
  const financed = Math.max(0, total - Number(downPayment));
  const rows = computeInstallments(financed, installments, firstDue);
  const rowsTotal = installmentsSum(rows);
  const rowsMatch = Math.abs(rowsTotal - Math.round(financed * 100) / 100) < 0.005;

  const setProduct = (i, k, v) => { const p = [...products]; p[i] = { ...p[i], [k]: v }; setProducts(p); };

  // Diff resumo
  const oldProducts = orig?.products || [];
  const newProducts = products.map((p) => ({ name: p.name, qty: Number(p.qty), price: Number(p.price), product_id: p.product_id || undefined, barcode: p.barcode || undefined, price_reason: p.price_reason || undefined }));
  const removed = oldProducts.filter((p) => !newProducts.some((n) => n.name === p.name && n.qty === p.qty && n.price === p.price));
  const added = newProducts.filter((p) => !oldProducts.some((o) => o.name === p.name && o.qty === p.qty && o.price === p.price));
  const oldTotal = orig?.total || 0;
  const difference = total - oldTotal;
  const oldFirstDue = orig?.installments?.[0]?.due_date?.slice(0, 10) || "";
  const hadPendingPix = (orig?.installments || []).some((i) => i.status !== "paid");

  const openConfirm = () => {
    if (products.length === 0 || total <= 0) { toast.error("Adicione ao menos um produto valido"); return; }
    if (!rowsMatch) { toast.error("A soma das parcelas difere do valor a financiar"); return; }
    setShowConfirm(true);
  };

  const save = async () => {
    if (!reason) { toast.error("Selecione o motivo da alteracao"); return; }
    if (reason === "Outro" && !reasonDetail.trim()) { toast.error("Descreva o motivo (Outro)"); return; }
    setSaving(true);
    try {
      const payload = {
        products: newProducts, installments: Number(installments), payment_method: method,
        down_payment: Number(downPayment), first_due_date: firstDue || undefined,
        sale_date: saleDate || undefined, discount: Number(discount) || 0, surcharge: Number(surcharge) || 0,
        notes: notes || "", reason, reason_detail: reasonDetail || "",
        device: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 200) : "",
      };
      const { data } = await api.put(`/sales/${saleId}/edit`, payload);
      toast.success(data.pix_regenerated ? "Venda atualizada. Novo PIX gerado e cliente avisado." : "Venda atualizada com sucesso");
      nav(`/vendas/${saleId}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : Array.isArray(detail) ? detail.map((d) => d.msg).join("; ")
        : "Erro ao salvar edicao";
      toast.error(msg);
      setSaving(false);
    }
  };

  if (!sale) return <div className="text-sm tracking-widest uppercase text-[#5C5A57]">Carregando...</div>;
  if (!sale.can_edit) {
    return (
      <div data-testid="edit-sale-blocked" className="max-w-xl">
        <div className="border-2 border-[#B4564F] bg-[#FBEDED] p-6">
          <div className="flex items-center gap-2 text-[#8B5E5E] font-medium mb-2"><WarningCircle size={20} weight="fill" /> Edicao nao permitida</div>
          <p className="text-sm text-[#5C5A57]">
            {sale.edit_locked_by_payment
              ? "Esta venda já possui pagamento confirmado. Utilize o módulo de Ajuste Financeiro (em breve) para alterar valores. Nenhum dado financeiro já pago pode ser alterado diretamente."
              : "Voce nao tem permissao para editar esta venda."}
          </p>
          <button onClick={() => nav(`/vendas/${saleId}`)} className="brand mt-4" data-testid="back-to-sale-btn">Voltar para a venda</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="edit-sale-page">
      <div className="topbar">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Editar venda</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">#{sale.number} · {sale.client_name}</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <section className="border border-[#EBE5DF] bg-white p-6">
            <h2 className="section-title mb-4">Dados da venda</h2>
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Data da venda</label>
            <input type="date" className="input-underline" value={saleDate} onChange={(e) => setSaleDate(e.target.value)} data-testid="edit-sale-date-input" />
          </section>

          <section className="border border-[#EBE5DF] bg-white p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="section-title">Produtos</h2>
              <button type="button" onClick={() => setProducts([...products, { name: "", qty: 1, price: 0 }])} className="text-xs tracking-widest uppercase font-medium hover:underline flex items-center gap-1" data-testid="edit-add-product-btn"><Plus size={14} /> Adicionar manual</button>
            </div>
            <ProductPicker onPick={addFromCatalog} testidPrefix="edit-sale-picker" />
            <div className="space-y-6">
              {products.map((p, i) => (
                <div key={i} className="border border-[#EBE5DF] bg-[#F5F0EB] p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Item #{i + 1}</div>
                    {products.length > 1 && (
                      <button type="button" onClick={() => setProducts(products.filter((_, k) => k !== i))} className="text-[#8B5E5E] hover:text-[#5C2D2B]" data-testid={`edit-remove-product-${i}`}><Trash size={16} /></button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div className="md:col-span-7">
                      <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Nome do produto *</label>
                      <input className="input-underline w-full" value={p.name} onChange={(e) => setProduct(i, "name", e.target.value)} readOnly={!!p.product_id} data-testid={`edit-product-name-${i}`} />
                      {p.product_id && <div className="text-[10px] text-[#5C5A57] mt-1">{p.codigo_interno ? `${p.codigo_interno} · ` : ""}Catálogo{p.barcode ? ` · cód. ${p.barcode}` : ""}</div>}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Qtd *</label>
                      <input className="input-underline tabular w-full" type="number" min="1" value={p.qty} onChange={(e) => setProduct(i, "qty", e.target.value)} data-testid={`edit-product-qty-${i}`} />
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Valor unitario *</label>
                      <BRLInput value={p.price} onChange={(v) => setProduct(i, "price", v)} disabled={!!p.product_id && !canEditPrice} testid={`edit-product-price-${i}`} />
                    </div>
                  </div>
                  {p.product_id && p.catalog_price != null && Math.abs(Number(p.price) - Number(p.catalog_price)) > 0.005 && (
                    <div className="mt-3">
                      <label className="block text-[10px] tracking-widest uppercase text-[#8B5E5E] mb-1">Motivo da alteração de preço (base: {brl(p.catalog_price)})</label>
                      <input className="input-underline w-full" value={p.price_reason || ""} onChange={(e) => setProduct(i, "price_reason", e.target.value)} placeholder="Descreva o motivo" data-testid={`edit-product-price-reason-${i}`} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="border border-[#EBE5DF] bg-white p-6">
            <h2 className="section-title mb-4">Observacoes</h2>
            <textarea rows={3} className="w-full p-3 border border-[#EBE5DF] text-sm" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="edit-sale-notes-input" />
          </section>
        </div>

        <aside>
          <section className="border border-[#EBE5DF] bg-[#F5F0EB] p-6 sticky top-6">
            <h2 className="section-title mb-6">Resumo</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span className="tabular font-medium">{brl(subtotal)}</span></div>
              <div className="flex justify-between items-center"><span>Desconto</span><div style={{ maxWidth: 160 }}><BRLInput value={discount} onChange={setDiscount} testid="edit-discount-input" /></div></div>
              <div className="flex justify-between items-center"><span>Acrescimo</span><div style={{ maxWidth: 160 }}><BRLInput value={surcharge} onChange={setSurcharge} testid="edit-surcharge-input" /></div></div>
              <div className="flex justify-between border-t border-[#EBE5DF] pt-3"><span>Total</span><span className="tabular font-medium" data-testid="edit-sale-total">{brl(total)}</span></div>
              <div className="flex justify-between items-center"><span>Entrada</span><div style={{ maxWidth: 160 }}><BRLInput value={downPayment} onChange={setDownPayment} testid="edit-down-payment-input" /></div></div>
              <div className="flex justify-between border-t border-[#EBE5DF] pt-3"><span>A financiar</span><span className="tabular font-bold">{brl(financed)}</span></div>

              <div className="pt-3">
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Parcelas</label>
                <select className="input-underline" value={installments} onChange={(e) => setInstallments(e.target.value)} data-testid="edit-installments-select">
                  {[1, 2, 3, 4, 5, 6, 8, 10, 12].map((n) => <option key={n} value={n}>{n}x de {brl(financed / n)}</option>)}
                </select>
              </div>
              <div className="pt-3">
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">1º vencimento</label>
                <input type="date" className="input-underline" value={firstDue} onChange={(e) => setFirstDue(e.target.value)} data-testid="edit-first-due-input" />
              </div>
              <div className="pt-3">
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Forma de pagamento</label>
                <select className="input-underline" value={method} onChange={(e) => setMethod(e.target.value)} data-testid="edit-method-select">
                  <option value="pix">PIX</option><option value="cash">Dinheiro</option><option value="card">Cartao</option><option value="link">Link</option><option value="boleto">Boleto</option>
                </select>
              </div>

              {financed > 0 && (
                <div className="pt-3 border-t border-[#EBE5DF]">
                  {rows.map((r) => (
                    <div key={r.number} className="flex justify-between items-center text-sm">
                      <span className="text-[#5C5A57]">Parcela {r.number} <span className="text-[10px]">· venc. {formatDate(toISODate(r.dueDate))}</span></span>
                      <span className="tabular font-medium">{brl(r.amount)}</span>
                    </div>
                  ))}
                  {!rowsMatch && <div className="mt-2 p-2 bg-[#F4E6E5] border border-[#DDA7A5] text-[11px] text-[#7A2D2B]">A soma das parcelas difere do valor a financiar.</div>}
                </div>
              )}
            </div>
            <button onClick={openConfirm} className="brand w-full mt-6" disabled={!rowsMatch} data-testid="review-edit-btn">Revisar alteracoes</button>
            <button onClick={() => nav(`/vendas/${saleId}`)} className="w-full mt-2 text-xs tracking-widest uppercase text-[#5C5A57] hover:underline" data-testid="cancel-edit-btn">Cancelar</button>
          </section>
        </aside>
      </div>

      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="edit-confirm-modal">
          <div className="bg-white max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="font-display text-2xl font-bold mb-4">Resumo das alteracoes</h3>
            <div className="space-y-3 text-sm">
              {removed.length > 0 && (
                <div><div className="text-[10px] tracking-widest uppercase text-[#8B5E5E] mb-1">Produtos removidos</div>
                  {removed.map((p, i) => <div key={i} className="text-[#5C5A57]" data-testid={`removed-product-${i}`}>- {p.name} ({p.qty}x · {brl(p.price)})</div>)}</div>
              )}
              {added.length > 0 && (
                <div><div className="text-[10px] tracking-widest uppercase text-[#3F6B45] mb-1">Produtos adicionados</div>
                  {added.map((p, i) => <div key={i} className="text-[#5C5A57]" data-testid={`added-product-${i}`}>+ {p.name} ({p.qty}x · {brl(p.price)})</div>)}</div>
              )}
              <div className="flex justify-between border-t border-[#EBE5DF] pt-3"><span>Valor anterior</span><span className="tabular" data-testid="confirm-old-value">{brl(oldTotal)}</span></div>
              <div className="flex justify-between"><span>Novo valor</span><span className="tabular font-bold" data-testid="confirm-new-value">{brl(total)}</span></div>
              <div className="flex justify-between"><span>Diferenca</span><span className={`tabular font-bold ${difference >= 0 ? "text-[#3F6B45]" : "text-[#B4564F]"}`} data-testid="confirm-difference">{difference >= 0 ? "+" : ""}{brl(difference)}</span></div>
              {oldFirstDue !== firstDue && <div className="flex justify-between"><span>Novo vencimento</span><span className="tabular">{formatDate(oldFirstDue)} → {formatDate(firstDue)}</span></div>}
              {hadPendingPix && <div className="text-[11px] text-[#2D6CB4] bg-[#EEF4FB] p-2">Um novo PIX sera gerado e o cliente sera avisado no WhatsApp.</div>}

              <div className="border-t border-[#EBE5DF] pt-3">
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Motivo da alteracao *</label>
                <select className="input-underline" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="edit-reason-select">
                  <option value="">Selecione...</option>
                  {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                {reason === "Outro" && (
                  <input className="input-underline mt-2" placeholder="Descreva o motivo" value={reasonDetail} onChange={(e) => setReasonDetail(e.target.value)} data-testid="edit-reason-detail-input" />
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowConfirm(false)} className="flex-1 text-xs tracking-widest uppercase font-medium border border-[#EBE5DF] py-3" data-testid="confirm-cancel-btn">Voltar</button>
              <button onClick={save} disabled={saving || !reason} className="brand flex-1" data-testid="confirm-save-btn">{saving ? "Salvando..." : "Confirmar e salvar"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
